/**
 * Location Alerts Section Component
 * 
 * Simplified component - just displays favorite locations list
 * Alert frequency controls moved to Notification Settings
 */

import React, { useState, useEffect, useContext } from 'react';
import { FaPlus } from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import FavoriteLocationsList from './FavoriteLocationsList';
import AddLocationModal from './AddLocationModal';
import './LocationAlertsSection.css';

// Get max locations based on user plan
const getMaxLocations = (planType) => {
  const limits = {
    'free': 1,
    'basic': 3,
    'premium': 10,
    'business': 10
  };
  
  const normalizedPlan = (planType || 'free').toLowerCase();
  return limits[normalizedPlan] || 1; // Default to free tier limit
};

const LocationAlertsSection = () => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  // Get user's plan and max locations
  const userPlan = user?.profile?.plan_type || 'free';
  const maxLocations = getMaxLocations(userPlan);
  const isPremiumOrBusiness = userPlan === 'premium' || userPlan === 'business';

  // Load user's favorite locations
  useEffect(() => {
    if (user?.id) {
      loadLocations();
    }
  }, [user]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('user_favorite_locations')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setLocations(data || []);
      
    } catch (error) {
      console.error('Error loading favorite locations:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.data_fetch_failed')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = () => {
    // Check if user has reached their limit
    if (locations.length >= maxLocations && !isPremiumOrBusiness) {
      const upgradePlan = userPlan === 'free' ? 'Basic (3 locations) or Premium (10 locations)' : 'Premium (10 locations)';
      addToast({
        type: 'warning',
        title: 'Location Limit Reached',
        description: `${userPlan === 'free' ? 'Free' : 'Basic'} plan allows up to ${maxLocations} location${maxLocations > 1 ? 's' : ''}. Upgrade to ${upgradePlan} for more!`
      });
      return;
    }
    
    setEditingLocation(null);
    setShowAddModal(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setShowAddModal(true);
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm(t('settings.confirm_delete_location') || 'Are you sure you want to delete this location?')) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('user_favorite_locations')
        .update({ is_active: false })
        .eq('id', locationId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Remove from local state
      setLocations(locations.filter(loc => loc.id !== locationId));
      
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: t('settings.location_updated')
      });
      
    } catch (error) {
      console.error('Error deleting location:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('settings.location_update_failed')
      });
    }
  };

  const handleLocationSaved = (savedLocation) => {
    if (editingLocation) {
      // Update existing location
      setLocations(locations.map(loc => 
        loc.id === savedLocation.id ? savedLocation : loc
      ));
    } else {
      // Add new location
      setLocations([savedLocation, ...locations]);
    }
    
    setShowAddModal(false);
    setEditingLocation(null);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingLocation(null);
  };

  // Check if user has reached their limit
  const hasReachedLimit = locations.length >= maxLocations && !isPremiumOrBusiness;

  // Get plan display name
  const getPlanDisplayName = () => {
    return userPlan.charAt(0).toUpperCase() + userPlan.slice(1);
  };

  return (
    <div className="favorite-locations-container">
      <div className="favorite-locations-header">
        <h4>📍 {t('settings.favorite_locations')}</h4>
        <p className="help-text">
          {t('settings.favorite_locations_help')}
        </p>
      </div>

      {/* Locations List */}
      {loading ? (
        <div className="locations-loading">
          <Spinner size="medium" />
        </div>
      ) : (
        <>
          <FavoriteLocationsList
            locations={locations}
            onEdit={handleEditLocation}
            onDelete={handleDeleteLocation}
          />

          {/* Add Location Button */}
          <div className="add-location-container">
            <button
              className="add-location-btn"
              onClick={handleAddLocation}
              disabled={hasReachedLimit}
            >
              <FaPlus /> {t('settings.add_location')}
            </button>
            
            {hasReachedLimit && (
              <p className="limit-message">
                ⚠️ {getPlanDisplayName()} plan allows up to {maxLocations} location{maxLocations > 1 ? 's' : ''}. 
                {userPlan === 'free' && ' Upgrade to Basic for 3 locations or Premium for 10 locations!'}
                {userPlan === 'basic' && ' Upgrade to Premium for 10 locations!'}
              </p>
            )}
            
            <p className="locations-count">
              {isPremiumOrBusiness 
                ? t('settings.locations_count', { current: locations.length, max: '10' })
                : t('settings.locations_count', { current: locations.length, max: maxLocations })}
            </p>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      <AddLocationModal
        isOpen={showAddModal}
        location={editingLocation}
        onSave={handleLocationSaved}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default LocationAlertsSection;
